import axios from 'axios'
import dayjs, { Dayjs } from 'dayjs'
import { IntlShape } from 'react-intl'
import {
	AllowedReservationTimeSlot,
	BaseCheckboxGroupCustomOption,
	GetCalendarEventsReservationAvailableResponse,
	GetCalendarEventsReservationFirstAvailableDateParams,
	GetCartResponse,
	GetPaginatedCalendarEventsGroupReservationAvailableResponse,
	PostCartGroupReservationBody,
	PostCartReservationBody,
	PutCartGroupReservationBody,
	PutCartReservationBody,
	PriceAndDurationData,
	ReservationBookingEmployeeAndDateSelectionForm,
	ReservationBookingEmployeeAndDateSelectionSubmittedForm,
	SalonServiceResponse,
	ServiceCategoryParameterValueData,
	TimeSlotOption,
	TimeSlotOptionsByDayPeriod,
	TimeSlotsSubset
} from '../../types/types'
import {
	CART_RESERVATION_SUBMIT_ERROR_CODE,
	DEFAULT_DATE_FORMAT,
	RESERVATION_BOOKING_MODAL_FIXED_HEADER_HEIGHT,
	RESERVATIONS_TIME_SLOTS
} from '../../utils/enums'
import { TIME_SLOTS_TRANSLATIONS } from '../../utils/helper'
import { resolveReservationDeviceID, type ReservationDeviceStorage } from '../../utils/reservationDeviceId'
import { getIntl } from '../../utils/intl'

export type SelectedIndustryData = { industryID: string; image?: string }

export type AvailableDatesStateData = Record<string, GetCalendarEventsReservationAvailableResponse['availableReservations'] | null>

export type TimeSlotPeriods = {
	label: string
	value: AllowedReservationTimeSlot
	formName: keyof TimeSlotsSubset
}

export type EmployeeOption = BaseCheckboxGroupCustomOption<{ avatar?: string; priceAndDurationData: PriceAndDurationData | undefined }>

export const ANYONE_AVAILABLE_OPTION_VALUE = 'ANYONE_AVAILABLE_OPTION_VALUE'

export const EMPTY_TIME_SLOT_OPTIONS: TimeSlotOptionsByDayPeriod = {
	[RESERVATIONS_TIME_SLOTS.MORNING]: [],
	[RESERVATIONS_TIME_SLOTS.AFTERNOON]: [],
	[RESERVATIONS_TIME_SLOTS.EVENING]: []
}

export const getTimeSlotFormName = (dayPeriod: AllowedReservationTimeSlot): keyof TimeSlotsSubset => {
	return `timeSlot-${dayPeriod}`
}

export const TIME_SLOT_PERIODS = (timeSlotTranslations: ReturnType<typeof TIME_SLOTS_TRANSLATIONS>): TimeSlotPeriods[] => [
	{
		label: timeSlotTranslations[RESERVATIONS_TIME_SLOTS.MORNING],
		value: RESERVATIONS_TIME_SLOTS.MORNING,
		formName: getTimeSlotFormName(RESERVATIONS_TIME_SLOTS.MORNING)
	},
	{
		label: timeSlotTranslations[RESERVATIONS_TIME_SLOTS.AFTERNOON],
		value: RESERVATIONS_TIME_SLOTS.AFTERNOON,
		formName: getTimeSlotFormName(RESERVATIONS_TIME_SLOTS.AFTERNOON)
	},
	{
		label: timeSlotTranslations[RESERVATIONS_TIME_SLOTS.EVENING],
		value: RESERVATIONS_TIME_SLOTS.EVENING,
		formName: getTimeSlotFormName(RESERVATIONS_TIME_SLOTS.EVENING)
	}
]

type TimeSlotDataMapItem = { timeFrom: string; timeTo: string; employeeID: string; dayPeriod: AllowedReservationTimeSlot }

type TimeSlotDataMap = Record<string, TimeSlotDataMapItem[]>

type TimeSlotData = {
	timeSlotOptions: TimeSlotOptionsByDayPeriod
	timeSlotDataMap: TimeSlotDataMap
}

export const getSelectedTimeSlotValue = (
	selectedTimeSlotData: TimeSlotDataMapItem[] | null,
	timeSlotEmployee: ReservationBookingEmployeeAndDateSelectionForm['timeSlotEmployee']
): ReservationBookingEmployeeAndDateSelectionSubmittedForm['timeSlot'] | null => {
	if (!selectedTimeSlotData?.length) {
		return null
	}

	if (selectedTimeSlotData.length === 1) {
		return selectedTimeSlotData[0]
	}

	if (!timeSlotEmployee.length) {
		return null
	}

	const employeeID = timeSlotEmployee[0]
	return selectedTimeSlotData.find((data) => data.employeeID === employeeID) || null
}

export const getSelectedTimeSlotGroupValue = (
	options: GetPaginatedCalendarEventsGroupReservationAvailableResponse['availableReservations'] | [],
	selectedValue: string | null
) => {
	if (!selectedValue) return null

	const group = options.find((option) => option.items.some((item) => item.calendarEvent.id === selectedValue))
	if (!group) return null
	const found = group.items.find((item) => item.calendarEvent.id === selectedValue)
	return found ? { date: group.date, item: found } : null
}

export const getSelectedTimeSlotOption = (timeSlotPeriods: TimeSlotOption[][]): TimeSlotOption | null => {
	const selectedTimeSlot = timeSlotPeriods.find((period) => period.length > 0)
	return selectedTimeSlot ? selectedTimeSlot[0] : null
}

export const getTimeSlotData = (availableData: NonNullable<AvailableDatesStateData[number]>, date: Dayjs): TimeSlotData => {
	const timeSlotDataMap = availableData.reduce<TimeSlotDataMap>((acc, item) => {
		if (!dayjs(item.date).isSame(dayjs(date), 'date')) {
			return acc
		}

		return item.items.reduce<TimeSlotDataMap>((itemsAcc, currentItem) => {
			return currentItem.employeeIDs.reduce<TimeSlotDataMap>((innerAcc, employeeID) => {
				const newValue: TimeSlotDataMapItem = {
					timeFrom: currentItem.from,
					timeTo: currentItem.to,
					employeeID,
					dayPeriod: currentItem.dayPart as AllowedReservationTimeSlot
				}

				if (innerAcc[currentItem.from]) {
					return {
						...innerAcc,
						[currentItem.from]: [...innerAcc[currentItem.from], newValue]
					}
				}
				return {
					...innerAcc,
					[currentItem.from]: [newValue]
				}
			}, itemsAcc)
		}, {})
	}, {})

	const timeSlotOptions = Object.entries({ ...timeSlotDataMap }).reduce<TimeSlotOptionsByDayPeriod>((acc, [timeFrom, timeItems]) => {
		const item = { ...timeItems[0] }

		return {
			...acc,
			[item.dayPeriod]: [...acc[item.dayPeriod], { key: timeFrom, value: timeFrom, label: timeFrom, extra: { dayPeriod: item.dayPeriod } }]
		}
	}, EMPTY_TIME_SLOT_OPTIONS)

	return {
		timeSlotOptions,
		timeSlotDataMap
	}
}

const KEY_SEPARATOR = '___'

export const getCalendarGridKey = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs, employeeID: string) =>
	`${startDate.format(DEFAULT_DATE_FORMAT)}${KEY_SEPARATOR}${endDate.format(DEFAULT_DATE_FORMAT)}${KEY_SEPARATOR}${employeeID}`

export const parseCalendarGridKey = (key: string) => {
	const split = key.split(KEY_SEPARATOR)
	return { startDate: dayjs(split[0]), endDate: dayjs(split[1]), employeeID: split[2] }
}

export const getAnyoneAvailableOption = (
	selectedServiceData: SalonServiceResponse['service'],
	selectedServiceCategoryParameterValueData: ServiceCategoryParameterValueData | undefined
): EmployeeOption => {
	const intl = getIntl()

	return {
		key: ANYONE_AVAILABLE_OPTION_VALUE,
		value: ANYONE_AVAILABLE_OPTION_VALUE,
		label: intl.formatMessage({ id: 'Anyone available', defaultMessage: 'Anyone available' }),
		extra: {
			priceAndDurationData: selectedServiceCategoryParameterValueData
				? selectedServiceData.serviceCategoryParameter?.values.find(
						(value) => selectedServiceCategoryParameterValueData.categoryParameterValueID === value.categoryParameterValueID
					)?.priceAndDurationData
				: selectedServiceData.rangePriceAndDurationData
		}
	}
}

export const getEmployeeOption = (
	employee: SalonServiceResponse['service']['employees'][number],
	selectedServiceCategoryParameterValueData: ServiceCategoryParameterValueData | undefined
): EmployeeOption => {
	const intl = getIntl()
	return {
		key: employee.id,
		value: employee.id,
		label: employee.fullName || employee.email || employee.inviteEmail || intl.formatMessage({ id: 'Employee', defaultMessage: 'Employee' }),
		extra: {
			avatar: employee.image.resizedImages.thumbnail,
			priceAndDurationData: selectedServiceCategoryParameterValueData
				? employee.serviceCategoryParameter?.values.find(
						(value) => selectedServiceCategoryParameterValueData.categoryParameterValueID === value.categoryParameterValueID
					)?.priceAndDurationData
				: employee.rangePriceAndDurationData
		}
	}
}

export const getEmployeeOptions = (
	anyoneAvailableOption: EmployeeOption,
	selectedServiceData: SalonServiceResponse['service'],
	selectedServiceCategoryParameterValueData: ServiceCategoryParameterValueData | undefined,
	selectedEmployeeIDs?: string[]
) => {
	const options: EmployeeOption[] = []

	if (!selectedEmployeeIDs) {
		options.push(anyoneAvailableOption)
	}

	selectedServiceData.employees.forEach((employee) => {
		if (selectedEmployeeIDs && !selectedEmployeeIDs.includes(employee.id)) {
			return
		}

		options.push(getEmployeeOption(employee, selectedServiceCategoryParameterValueData))
	})

	return options
}

export const scrollElementIntoView = <T extends HTMLElement>(element: T) => {
	// element.scrollIntoView is used only as fallback, becauso we need to offset the scroll distance with the fixed header height
	// fallback is needed, because we are targeting data attribute from external library (overlayscrollbars), which could be changed with any release
	// unfortunately, the library doesn't provide a way to set custom class or id to this attribute

	const scrollWrapper = element?.closest<HTMLDivElement>('[data-overlayscrollbars-viewport]')

	if (scrollWrapper) {
		const headerOffset = RESERVATION_BOOKING_MODAL_FIXED_HEADER_HEIGHT + 10

		// Get the bounding rectangle of both the element and the scroll wrapper
		const elementRect = element.getBoundingClientRect()
		const scrollWrapperRect = scrollWrapper.getBoundingClientRect()

		// Calculate the element's position relative to the scrollWrapper
		const relativeTop = elementRect.top - scrollWrapperRect.top

		// Get the current scroll position of the scrollWrapper
		const currentScrollTop = scrollWrapper.scrollTop

		// Calculate the final scroll position with the offset
		const offsetPosition = currentScrollTop + relativeTop - headerOffset

		scrollWrapper.scrollTo({
			top: offsetPosition,
			behavior: 'smooth'
		})
	} else {
		element.scrollIntoView({ behavior: 'smooth' })
	}
}

export const RESERVATION_BOOKING_MODAL_Z_INDEXES = {
	MODAL_WRAP: 900,
	SELECT_DROPDOWN: 901
}

export const getEmployeeAndDateSelectionInitFormData = (
	submittedData: ReservationBookingEmployeeAndDateSelectionSubmittedForm
): ReservationBookingEmployeeAndDateSelectionForm => {
	let initData: ReservationBookingEmployeeAndDateSelectionForm = {
		employeeID: [submittedData.employeeID || ANYONE_AVAILABLE_OPTION_VALUE],
		date: dayjs(submittedData.date),
		'timeSlot-MORNING': [],
		'timeSlot-EVENING': [],
		'timeSlot-AFTERNOON': [],
		timeSlotEmployee: []
	}

	if (submittedData.timeSlot.timeFrom && submittedData.timeSlot.timeTo) {
		if (submittedData.timeSlot.employeeID && submittedData.employeeID !== submittedData.timeSlot.employeeID) {
			initData = {
				...initData,
				timeSlotEmployee: [submittedData.timeSlot.employeeID]
			}
		}

		const timeSlotValue: TimeSlotOption = {
			key: submittedData.timeSlot.timeFrom,
			value: submittedData.timeSlot.timeFrom,
			label: submittedData.timeSlot.timeFrom,
			extra: {
				dayPeriod: submittedData.timeSlot.dayPeriod
			}
		}
		initData[`timeSlot-${submittedData.timeSlot.dayPeriod}`].push(timeSlotValue)
	}

	return initData
}

type CartReservationRequestBodyArgs = {
	date: string
	timeFrom: string
	timeTo: string
	salonID: string
	employeeID: string
	serviceID: string
	serviceCategoryParameterValueID: string | null
}

type ResolveCartReservationDeviceIDArgs = {
	cartReservationDeviceData?: ReservationDeviceStorage | null
	setCartReservationDeviceData: (value: Partial<ReservationDeviceStorage>) => void
}

type SubmitCartReservationRequestArgs<TRequestBody> = ResolveCartReservationDeviceIDArgs & {
	postCartReservations: (deviceID: string, reqBody: TRequestBody) => Promise<unknown>
	requestBody: TRequestBody
}

export const getCartReservationRequestBody = (args: CartReservationRequestBodyArgs): PostCartReservationBody => {
	const { date, timeFrom, timeTo, salonID, employeeID, serviceID, serviceCategoryParameterValueID } = args

	return {
		start: {
			date,
			time: timeFrom
		},
		end: {
			date,
			time: timeTo
		},
		salonID,
		employeeID,
		serviceID,
		serviceCategoryParameterValueID
	}
}

export const getCartGroupReservationRequestBody = (calendarEventID: string): PostCartGroupReservationBody => {
	return {
		calendarEventID
	}
}

export const getPutCartReservationRequestBody = (oldReservationItemID: string, args: CartReservationRequestBodyArgs): PutCartReservationBody => ({
	oldReservationItemID,
	newReservationItemData: getCartReservationRequestBody(args)
})

export const getPutCartGroupReservationRequestBody = (oldReservationItemID: string, calendarEventID: string): PutCartGroupReservationBody => ({
	oldReservationItemID,
	newReservationItemData: {
		calendarEventID
	}
})

export const submitCartReservationRequest = async <TRequestBody>(args: SubmitCartReservationRequestArgs<TRequestBody>) => {
	const { postCartReservations, requestBody, cartReservationDeviceData, setCartReservationDeviceData } = args
	const deviceID = resolveReservationDeviceID({
		reservationDeviceData: cartReservationDeviceData ?? null,
		setReservationDeviceData: setCartReservationDeviceData
	})

	await postCartReservations(deviceID, requestBody)
	setCartReservationDeviceData({ deviceID, cartActive: true })
}

type SubmitPutCartReservationRequestArgs<TRequestBody> = ResolveCartReservationDeviceIDArgs & {
	putCartReservations: (deviceID: string, reqBody: TRequestBody) => Promise<unknown>
	requestBody: TRequestBody
}

export const submitPutCartReservationRequest = async <TRequestBody>(args: SubmitPutCartReservationRequestArgs<TRequestBody>) => {
	const { putCartReservations, requestBody, cartReservationDeviceData, setCartReservationDeviceData } = args
	const deviceID = resolveReservationDeviceID({
		reservationDeviceData: cartReservationDeviceData ?? null,
		setReservationDeviceData: setCartReservationDeviceData
	})

	await putCartReservations(deviceID, requestBody)
}

type CartReservationErrorResponse = {
	messages?: {
		code?: string
		message?: string
		type?: string
	}[]
}

export type EmptySlotAvailabilityResult = {
	employeeFirstAvailableDate: string | null
	anyoneFirstAvailableDate: string | null
}

export type GetFirstAvailableDateQueryParamsArgs = {
	serviceID: string
	serviceCategoryParameterValueID?: string | null
	employeeID: string
	dateFrom: string
}

export const getFirstAvailableDateQueryParams = ({
	serviceID,
	serviceCategoryParameterValueID,
	employeeID,
	dateFrom
}: GetFirstAvailableDateQueryParamsArgs): GetCalendarEventsReservationFirstAvailableDateParams => {
	return {
		dateFrom,
		serviceID,
		serviceCategoryParameterValueID,
		employeeID: employeeID === ANYONE_AVAILABLE_OPTION_VALUE ? undefined : employeeID
	}
}

export const resolveEmptySlotAvailability = async (
	getFirstAvailableDate: (
		salonID: string,
		query: GetCalendarEventsReservationFirstAvailableDateParams
	) => Promise<{ firstAvailableDate: string | null } | undefined>,
	salonID: string,
	args: GetFirstAvailableDateQueryParamsArgs & { hasMultipleEmployees: boolean }
): Promise<EmptySlotAvailabilityResult> => {
	const employeeResponse = await getFirstAvailableDate(salonID, getFirstAvailableDateQueryParams(args))
	const employeeFirstAvailableDate = employeeResponse?.firstAvailableDate ?? null
	const isSpecificEmployee = args.employeeID !== ANYONE_AVAILABLE_OPTION_VALUE

	if (!isSpecificEmployee || !args.hasMultipleEmployees || employeeFirstAvailableDate) {
		return {
			employeeFirstAvailableDate,
			anyoneFirstAvailableDate: null
		}
	}

	const anyoneResponse = await getFirstAvailableDate(
		salonID,
		getFirstAvailableDateQueryParams({
			...args,
			employeeID: ANYONE_AVAILABLE_OPTION_VALUE
		})
	)

	return {
		employeeFirstAvailableDate: null,
		anyoneFirstAvailableDate: anyoneResponse?.firstAvailableDate ?? null
	}
}

export const getCartReservationSubmitErrorCode = (error: unknown): CART_RESERVATION_SUBMIT_ERROR_CODE | null => {
	if (!axios.isAxiosError(error) || error.response?.status !== 409) {
		return null
	}

	const responseData = error.response?.data as CartReservationErrorResponse | undefined
	const code = responseData?.messages?.[0]?.code

	if (code === CART_RESERVATION_SUBMIT_ERROR_CODE.CART_IS_FULL) {
		return CART_RESERVATION_SUBMIT_ERROR_CODE.CART_IS_FULL
	}

	if (code === CART_RESERVATION_SUBMIT_ERROR_CODE.ALREADY_BOOKED) {
		return CART_RESERVATION_SUBMIT_ERROR_CODE.ALREADY_BOOKED
	}

	if (code === CART_RESERVATION_SUBMIT_ERROR_CODE.CART_EXPIRED) {
		return CART_RESERVATION_SUBMIT_ERROR_CODE.CART_EXPIRED
	}

	return null
}

export type CartReservationItemForUnavailableModal = GetCartResponse['cart']['cartReservationItems'][number]

export const getCartReservationTimeSlotLabel = (item: Pick<CartReservationItemForUnavailableModal, 'start' | 'end'>) => {
	const endTime = item.end.time

	return endTime ? `${item.start.time} - ${endTime}` : item.start.time
}

type GetUnavailableServicesModalDescriptionArgs = {
	unavailableItemIDs: string[]
	reservationItems: CartReservationItemForUnavailableModal[]
	formatMessage: IntlShape['formatMessage']
	fallbackServiceName: string
}

export const getUnavailableServicesModalDescription = ({
	unavailableItemIDs,
	reservationItems,
	formatMessage,
	fallbackServiceName
}: GetUnavailableServicesModalDescriptionArgs) => {
	if (unavailableItemIDs.length > 1) {
		return formatMessage({
			id: 'Some of your services have been booked. Please select another time slot, change the employee or choose another service.',
			defaultMessage: 'Some of your services have been booked. Please select another time slot, change the employee or choose another service.'
		})
	}

	const unavailableItem = reservationItems.find((item) => item.id === unavailableItemIDs[0])
	const serviceName = unavailableItem?.service.name || fallbackServiceName
	const timeSlot = unavailableItem ? getCartReservationTimeSlotLabel(unavailableItem) : ''

	return formatMessage(
		{
			id: 'Your service "{serviceName}" at {timeSlot} has been booked. Please select another time slot, change the employee or choose another service.',
			defaultMessage:
				'Your service "{serviceName}" at {timeSlot} has been booked. Please select another time slot, change the employee or choose another service.'
		},
		{ serviceName, timeSlot }
	)
}
